import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSearchConfig } from "../hooks/useSearchConfig";
import { useNavigate } from "react-router-dom";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building, Sparkles } from "lucide-react";
import { Vision360Map, Vision360MapRef } from "../components/Vision360Map";
import { Vision360Card } from "../components/Vision360Card";
import { Vision360Drawer } from "../components/Vision360Drawer";
import { Card } from "../types";
import { SelectionProvider, useSelection } from "../contexts/SelectionContext";
import { Skeleton } from "@/components/ui/skeleton";
import { visionService } from "@/services/vision-service";
import { VirtuosoGrid } from "react-virtuoso";
import { getDefaultPropertyType } from "../constants/property-types";
import { LongListSelectionFooter } from "../components/long-list-selection-footer";
import { LongListFeedbackModal } from "../components/long-list-feedback-modal";
import { Vision360ProposalModal } from "../components/modal-proposal-private-room";
import { Vision360ProposalLinksModal } from "../components/modal-proposal-links";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";

const DEFAULT_DISTANCE = 30;
const SHOW_SELECTION_FOOTER = true;

function LongListContent() {
  const { config, saveConfig } = useSearchConfig();
  const { selectAll } = useSelection();
  const navigate = useNavigate();
  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerCard, setDrawerCard] = useState<Card | null>(null);
  const [cardToCenter, setCardToCenter] = useState<string | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
  const [isProposalLinksModalOpen, setIsProposalLinksModalOpen] =
    useState(false);
  const [proposalData, setProposalData] = useState<{
    proposalLongUrl: string;
    proposalShortUrl: string;
    privateRoomsIds: string[];
  } | null>(null);
  const mapRef = useRef<Vision360MapRef>(null);
  const virtuosoRef = useRef(null);
  const hasInitializedSelection = useRef(false);

  const longListId = new URLSearchParams(window.location.search).get("l_id");

  const getArrayPictures = (
    image0: string | null,
    image1: string | null,
    image2: string | null,
    image3: string | null | undefined,
    image4: string | null | undefined,
    image5: string | null | undefined,
  ) => {
    const pictures: Array<string> = [];

    if (image0) {
      pictures.push(image0);
    }
    if (image1) {
      pictures.push(image1);
    }
    if (image2) {
      pictures.push(image2);
    }
    if (image3) {
      pictures.push(image3);
    }
    if (image4) {
      pictures.push(image4);
    }
    if (image5) {
      pictures.push(image5);
    }

    return pictures;
  };

  useEffect(() => {
    const loadCards = async () => {
      try {
        if (!longListId) {
          setIsLoading(false);
          return;
        }
        const dataResponse = await visionService.getLongListDetail(longListId);

        if (!dataResponse) {
          setIsLoading(false);
          return;
        }

        const selectedCardsData = dataResponse.results || [];
        const convertedCards: Card[] = selectedCardsData
          .map((item: any) => {
            const details = item.details || {};
            const spaceDetails = item.space_details || item.space || {};

            const lat = spaceDetails.latitude
              ? Number(spaceDetails.latitude)
              : 0;
            const lng = spaceDetails.longitude
              ? Number(spaceDetails.longitude)
              : 0;

            const itemId =
              details.id?.toString() ||
              item.private_room_id ||
              item.id?.toString() ||
              "";
            const spaceId =
              spaceDetails.id?.toString() ||
              details.space_id?.toString() ||
              item.space_id?.toString() ||
              "";

            return {
              id: itemId,
              search_id: item.search_id,
              title: details.label || item.label || "",
              address: spaceDetails.address || "",
              neighborhood: spaceDetails.neighborhood || undefined,
              cityname: spaceDetails.cityname || undefined,
              space: {
                name: spaceDetails.name || "",
                id: spaceId,
              },
              capacity: item.capacity ?? details.capacity ?? 0,
              credits: item.monthly_credit_price ?? item.credits ?? 0,
              monthly_price: item.monthly_price ?? details.monthly_price ?? 0,
              status: item.status ?? details.status ?? 0,
              size: item.size ?? details.size ?? undefined,
              updated_at: details.updated_at || details.data_updated_at || "",
              images: getArrayPictures(
                details.picture_url,
                details.picture_url1,
                details.picture_url2,
                details.picture_url3,
                details.picture_url4,
                details.picture_url5,
              ),
              original_url: item.dpp || "",
              coords: {
                lat: !isNaN(lat) && lat !== 0 ? lat : 0,
                lng: !isNaN(lng) && lng !== 0 ? lng : 0,
              },
              match_percentage: item.match_percent,
              score: item.detailed_score_analysis?.map((scoreItem: any) => ({
                criterion: scoreItem.criterion,
                requested: scoreItem.requested,
                offered: scoreItem.offered,
                matched: scoreItem.matched,
              })) as any,
              score_details: item?.score_details,
              type: "internal",
              space_id:
                item.space_id?.toString() ||
                spaceDetails.id?.toString() ||
                undefined,
              private_room_id: item.private_room_id,
              monthly_credit_price: item.monthly_credit_price,
              days_since_update: item.days_since_update,
              threshold_days: item.threshold_days ?? undefined,
              is_outdated: item.is_outdated ?? undefined,
              distance_km: item.distance_km ?? undefined,
              amenities: item.amenities || details.amenities || undefined,
              attributes: item.attributes || undefined,
              details:
                details && Object.keys(details).length > 0
                  ? {
                      id: details.id,
                      search_id: item.search_id ?? undefined,
                      space_id:
                        details.space_id?.toString() ||
                        item.space_id?.toString() ||
                        undefined,
                      label: details.label,
                      size: details.size ?? undefined,
                      capacity: details.capacity,
                      monthly_price: details.monthly_price,
                      yearly_price: details.yearly_price ?? undefined,
                      amenities: details.amenities || undefined,
                      extra_services: details.extra_services || undefined,
                      shared_amenities: details.shared_amenities || undefined,
                      shared_extra_services:
                        details.shared_extra_services || undefined,
                      advantages: details.advantages || undefined,
                      picture_url: details.picture_url || undefined,
                      picture_url1: details.picture_url1 || undefined,
                      picture_url2: details.picture_url2 || undefined,
                      picture_url3: details.picture_url3 || undefined,
                      picture_url4: details.picture_url4 || undefined,
                      picture_url5: details.picture_url5 || undefined,
                      notes: details.notes || undefined,
                      status: details.status ?? undefined,
                      category: details.category ?? undefined,
                      created_at: details.created_at || undefined,
                      updated_at:
                        details.updated_at ||
                        details.data_updated_at ||
                        undefined,
                      deleted_at: details.deleted_at || undefined,
                      created_by_user_id:
                        details.created_by_user_id || undefined,
                      private_office_amenities_attributes:
                        details.grouped_amenities
                          ? details.grouped_amenities.flatMap(
                              (group: any) =>
                                group.amenities?.map((amenity: any) => ({
                                  amenity: {
                                    name: amenity.name,
                                    category: group.name || "",
                                    slug:
                                      amenity.name
                                        ?.toLowerCase()
                                        .replace(/\s+/g, "-") || "",
                                  },
                                  private_office_attribute:
                                    amenity.attributes || {},
                                })) || [],
                            )
                          : undefined,
                      status_updated_at: details.data_updated_at || undefined,
                    }
                  : undefined,
              space_details:
                spaceDetails && Object.keys(spaceDetails).length > 0
                  ? {
                      id: spaceDetails.id,
                      acknowledge_date:
                        spaceDetails.acknowledge_date || undefined,
                      name: spaceDetails.name,
                      enabled: spaceDetails.enabled ?? undefined,
                      type: spaceDetails.type ?? undefined,
                      latitude: spaceDetails.latitude || undefined,
                      longitude: spaceDetails.longitude || undefined,
                      cityname: spaceDetails.cityname || undefined,
                      neighborhood: spaceDetails.neighborhood || undefined,
                      address: spaceDetails.address || undefined,
                    }
                  : undefined,
            } as Card;
          })
          .filter(
            (card: Card) => card.coords.lat !== 0 && card.coords.lng !== 0,
          );

        if (convertedCards.length === 0) {
          setCards([]);
          setIsLoading(false);
          return;
        }

        setCards(convertedCards);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading buildings:", error);
        setCards([]);
        setIsLoading(false);
      }
    };

    loadCards();
  }, [longListId]);

  const handleBack = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackConfirm = () => {
    if (config) {
      const defaultFilters = {
        query: "",
        propertyType: getDefaultPropertyType(),
        isFurnished: false,
        budgetMin: "" as number | "",
        budgetMax: "" as number | "",
        sizeMin: "" as number | "",
        sizeMax: "" as number | "",
        positionsMin: "" as number | "",
        positionsMax: "" as number | "",
        minPrice: undefined,
        maxPrice: undefined,
        minCapacity: undefined,
        maxCapacity: undefined,
        privateOfficeStatus: ["0"],
        amenitiesSlugs: [],
        amenitiesSlugsOptional: [],
        distance: DEFAULT_DISTANCE,
        unitCreditPrice: 80,
        capacityWeight: undefined,
        priceWeight: undefined,
        searchType: undefined,
        location: undefined,
        latitude: undefined,
        longitude: undefined,
        spaceId: undefined,
        privateOfficeIds: undefined,
        cityName: undefined,
        neighborhood: undefined,
      };

      saveConfig({
        currentStep: "setup",
        filters: defaultFilters,
        selectedCards: [],
        longListSelectedCards: [],
        longListId: null,
        shortListId: null,
      });
    }
    setIsFeedbackModalOpen(false);
    navigate(DEFAULT_ROUTES.VISION_360_BETA_SETUP);
  };

  const handleMarkerClick = useCallback((card: Card) => {
    setCardToCenter(card.id);
  }, []);

  const handleCenterOnMap = useCallback((card: Card) => {
    if (mapRef.current) {
      mapRef.current.centerOnCard(card);
    }
  }, []);

  useEffect(() => {
    if (cardToCenter && cards.length > 0) {
      const index = cards.findIndex((card) => card.id === cardToCenter);
      if (index !== -1) {
        setTimeout(() => {
          if (virtuosoRef.current) {
            // @ts-expect-error - VirtuosoGrid ref type
            virtuosoRef.current.scrollToIndex({
              index,
              align: "center",
              behavior: "smooth",
            });
          }
        }, 100);
      }
      setCardToCenter(null);
    }
  }, [cardToCenter, cards]);

  const memoizedCards = useMemo(() => {
    const validCards = cards.filter(
      (card) =>
        card.coords?.lat &&
        card.coords?.lng &&
        !isNaN(card.coords.lat) &&
        !isNaN(card.coords.lng) &&
        card.coords.lat !== 0 &&
        card.coords.lng !== 0,
    );
    return validCards;
  }, [cards]);

  useEffect(() => {
    if (
      memoizedCards.length > 0 &&
      !hasInitializedSelection.current &&
      !isLoading
    ) {
      const cardIds = memoizedCards.map((c) => c.id);
      selectAll(cardIds);
      hasInitializedSelection.current = true;
    }
  }, [memoizedCards, selectAll, isLoading]);

  const handleProposalSuccess = useCallback(
    (data: {
      proposalLongUrl: string;
      proposalShortUrl: string;
      privateRoomsIds: string[];
    }) => {
      setProposalData(data);
      setIsProposalModalOpen(false);
      setIsProposalLinksModalOpen(true);

      sendTrackDataServer({
        eventName: "VISION_CREATE_PROPOSAL_FINISHED",
        data: {
          deal_id: config?.dealId?.toString() ?? null,
          private_room_ids: data.privateRoomsIds,
        },
        url: location.pathname,
        productType: "PO",
      });
    },
    [config?.dealId],
  );

  const handleCloseProposalModal = useCallback(() => {
    setIsProposalModalOpen(false);
  }, []);

  const handleCloseProposalLinksModal = useCallback(() => {
    setIsProposalLinksModalOpen(false);
    setProposalData(null);
  }, []);

  const handleContinue = useCallback(() => {
    setIsProposalModalOpen(true);
  }, []);

  return (
    <>
      <div className="relative h-[calc(100vh-4rem)] flex flex-row">
        <div className="w-full md:w-2/3 flex flex-col h-full p-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-bold">Long List</h1>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4" />
              <span className="hidden md:inline">Powered by AI</span>
            </div>
          </div>

          <div
            className={`flex-1 min-h-0 relative ${SHOW_SELECTION_FOOTER ? "mb-16" : ""}`}
          >
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="border rounded-lg bg-white shadow-sm overflow-hidden flex flex-col h-full"
                  >
                    <div className="relative h-40 w-full flex-shrink-0">
                      <Skeleton className="w-full h-full" />
                    </div>
                    <div className="p-4 flex flex-col flex-1 min-h-0">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2 mb-4" />
                      <Skeleton className="h-8 w-1/3 mb-2" />
                      <Skeleton className="h-4 w-2/3 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : memoizedCards.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <Building className="h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {cards.length === 0
                    ? "Nenhum resultado encontrado"
                    : "Nenhum imóvel com localização válida"}
                </h3>
                <p className="text-gray-500">
                  {cards.length === 0
                    ? "Esta long list não possui resultados disponíveis."
                    : "Os imóveis selecionados não possuem coordenadas válidas para exibição no mapa."}
                </p>
              </div>
            ) : (
              <VirtuosoGrid
                ref={virtuosoRef}
                data={memoizedCards}
                totalCount={memoizedCards.length}
                itemContent={(_, card) => (
                  <Vision360Card
                    key={card.id}
                    card={card}
                    onOpenDrawer={setDrawerCard}
                    showSelectButton={SHOW_SELECTION_FOOTER}
                    onCenterOnMap={handleCenterOnMap}
                    showCenterOnMapButton={true}
                  />
                )}
                overscan={400}
                listClassName="grid grid-cols-1 md:grid-cols-2 gap-4"
                itemClassName="w-full h-full"
              />
            )}
          </div>
        </div>
        <div className="hidden md:block md:w-1/3 relative z-0 h-full">
          {isLoading ? (
            <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <Skeleton className="w-16 h-16 rounded-full" />
                <div className="flex flex-col items-center gap-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            </div>
          ) : (
            <Vision360Map
              ref={mapRef}
              cards={memoizedCards}
              onOpenDrawer={setDrawerCard}
              customBottomPosition={SHOW_SELECTION_FOOTER ? 70 : 0}
              onMarkerClick={handleMarkerClick}
            />
          )}
        </div>
        {memoizedCards.length > 0 && SHOW_SELECTION_FOOTER && (
          <LongListSelectionFooter
            totalCount={memoizedCards.length}
            cards={memoizedCards}
            onContinue={handleContinue}
            isLoading={false}
            dealId={config?.dealId?.toString() ?? null}
            longListId={longListId}
          />
        )}
        <Vision360Drawer
          open={!!drawerCard}
          card={drawerCard}
          onClose={() => setDrawerCard(null)}
          showSelectButton={SHOW_SELECTION_FOOTER}
          dealId={config?.dealId?.toString() ?? null}
        />
        <LongListFeedbackModal
          open={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
          onConfirm={handleFeedbackConfirm}
          longListId={longListId}
          dealId={config?.dealId?.toString() ?? null}
        />
        <Vision360ProposalModal
          open={isProposalModalOpen}
          origin="long-list-hubspot"
          parentLongListId={longListId ?? null}
          onClose={handleCloseProposalModal}
          onSuccess={handleProposalSuccess}
          allCards={memoizedCards}
          dealId={config?.dealId?.toString()}
          filters={config?.filters}
        />
        <Vision360ProposalLinksModal
          open={isProposalLinksModalOpen}
          onClose={handleCloseProposalLinksModal}
          proposalLongUrl={proposalData?.proposalLongUrl || ""}
          proposalShortUrl={proposalData?.proposalShortUrl || ""}
          privateRoomsIds={proposalData?.privateRoomsIds || []}
          dealId={config?.dealId?.toString()}
        />
      </div>
    </>
  );
}

export default function LongListLayout() {
  const { saveConfig } = useSearchConfig();

  return (
    <SelectionProvider
      initialSelection={new Set()}
      onSelectionChange={(selectedIds) => {
        saveConfig({ longListSelectedCards: Array.from(selectedIds) });
      }}
    >
      <LongListContent />
    </SelectionProvider>
  );
}
